import { useEffect, useState } from "react";
import { ChevronLeftIcon } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router";
import { toast } from "sonner";

import { EnlaceVolver } from "@/components/EnlaceVolver";
import { AvisoDeDudasQueBloquean } from "@/components/generacion/AvisoDeDudasQueBloquean";
import { AnalisisDelExpediente, type VistaDelAnalisis } from "@/components/generacion/AnalisisDelExpediente";
import { BannerDesdeSuUltimaSesion } from "@/components/generacion/BannerDesdeSuUltimaSesion";
import { CarrilDeFases, type FaseDelCarril, type NumeroDeFase } from "@/components/generacion/CarrilDeFases";
import { DocumentoDelInforme } from "@/components/generacion/DocumentoDelInforme";
import { RedaccionDelInforme } from "@/components/generacion/RedaccionDelInforme";
import { ValorizacionDelInforme } from "@/components/generacion/ValorizacionDelInforme";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/hooks/useAuth";
import { useCondiciones, useEmisiones } from "@/hooks/useEmision";
import { useAnalisis, useDudas, useHechos, useIniciarAnalisis } from "@/hooks/useGeneracion";
import { useRedaccionSincronizada } from "@/hooks/useRedaccion";
import { useValorizacion } from "@/hooks/useValorizacion";
import { useInforme } from "@/hooks/useInformes";
import { getApiErrorMessage } from "@/lib/api";
import { estadoDeLaFase4, type DestinoDeLaCondicion } from "@/lib/emision";
import { conteoDeDudas, estadoDeLaFase1, filtrarDudas, guardarUltimaVisita, leerUltimaVisita } from "@/lib/generacion";
import {
  estadoDelEncabezado,
  pendientesDeLaPantalla,
  type TonoDelEncabezado,
} from "@/lib/generacionResumen";
import { tieneTasacionEnCurso } from "@/lib/informes";
import { cn } from "@/lib/utils";
import { estadoDeLaFase3 } from "@/lib/redaccion";
import { estadoDeLaFase2 } from "@/lib/valorizacion";

/** El chip del encabezado: el color sale del tono y el punto lo acompaña (tokens soft y dot). */
const CHIP_DEL_ENCABEZADO: Record<TonoDelEncabezado, { chip: string; punto: string }> = {
  reposo: { chip: "bg-divisor text-ink-medio", punto: "bg-ink-ghost" },
  info: { chip: "bg-info-soft text-info-soft-foreground", punto: "bg-info-dot" },
  aviso: { chip: "bg-warning-soft text-warning-texto", punto: "bg-warning-dot" },
  ok: { chip: "bg-success-soft text-success-soft-foreground", punto: "bg-success-dot" },
};

/**
 * Generación del informe con IA: la fase 1 (analizar el expediente y responder
 * las dudas), la 2 (valorización), la 3 (redacción de las secciones) y la 4
 * (revisar la calidad y emitir el documento).
 *
 * Las fases NO se bloquean entre sí: solo la emisión (fase 4) exige que lo
 * demás esté resuelto, y lo dice con un checklist cuyas filas llevan a la
 * fase que corresponde. Por eso el carril es navegación libre.
 */
export function GeneracionDelInforme() {
  const { id: idDeLaRuta } = useParams();
  const informeId = Number(idDeLaRuta);
  const informe = useInforme(informeId);
  const { user } = useAuth();
  const usuarioId = user?.id ?? null;

  const analisis = useAnalisis(informeId);
  const corrida = analisis.data;
  // Las dudas y los hechos existen una vez que hubo un análisis; antes la hoja
  // todavía no se construyó.
  const hayCorrida = corrida !== undefined && corrida !== null && corrida.estado !== "en_curso";
  const dudasQuery = useDudas(informeId, hayCorrida);
  const hechosQuery = useHechos(informeId, hayCorrida);
  const iniciar = useIniciarAnalisis(informeId);
  // La valorización solo existe desde la aceptación: antes el endpoint responde 422.
  const valorizacion = useValorizacion(
    informeId,
    informe.data !== undefined && tieneTasacionEnCurso(informe.data.estado),
  );

  // La redacción también existe solo desde la aceptación. La tarjeta del carril
  // la lee siempre; el avance de la corrida lo sincroniza esta pantalla solo
  // mientras no se está mirando la fase 3 (ahí lo hace su propio componente).
  const tieneTasacion = informe.data !== undefined && tieneTasacionEnCurso(informe.data.estado);
  const [fase, setFase] = useState<NumeroDeFase>(1);
  const redaccion = useRedaccionSincronizada(informeId, tieneTasacion, fase !== 3);
  // La fase 4 y la tarjeta del carril leen las mismas condiciones de emisión.
  const condiciones = useCondiciones(informeId, tieneTasacion, fase === 4);
  const emisiones = useEmisiones(informeId, tieneTasacion);
  const navigate = useNavigate();
  // La sección que pidió abrir el checklist de la fase 4 (la primera ofensora).
  const [seccionInicial, setSeccionInicial] = useState<number | null>(null);

  const dudas = dudasQuery.data?.data ?? [];
  const hechos = hechosQuery.data ?? [];

  const [vista, setVista] = useState<VistaDelAnalisis>({ pestana: "dudas", filtro: "abiertas", dudaId: null });

  /* ----- Última visita: se lee una vez por usuario e informe y se guarda al salir. */
  const [ultimaVisita, setUltimaVisita] = useState<Date | null>(null);
  const [bannerCerrado, setBannerCerrado] = useState(false);

  useEffect(() => {
    if (usuarioId === null) return;

    setUltimaVisita(leerUltimaVisita(usuarioId, informeId));

    const recordar = () => guardarUltimaVisita(usuarioId, informeId, new Date());
    window.addEventListener("pagehide", recordar);

    return () => {
      window.removeEventListener("pagehide", recordar);
      recordar();
    };
  }, [usuarioId, informeId]);

  // Cuando el análisis termina mientras se mira la pantalla, las dudas y los
  // hechos recién existen: el cambio de `hayCorrida` los habilita y los trae.

  /** Lleva una fila del checklist de emisión al lugar que la resuelve. */
  function irADestino(destino: DestinoDeLaCondicion) {
    switch (destino.tipo) {
      case "fase":
        if (destino.fase === 1) setVista({ pestana: "dudas", filtro: destino.filtro, dudaId: destino.dudaId });
        if (destino.fase === 3) setSeccionInicial(destino.itemId);
        setFase(destino.fase);
        break;
      case "maestro":
        void navigate(
          destino.itemId === null ? "/admin/contenidos/maestro" : `/admin/contenidos/maestro?item=${destino.itemId}`,
        );
        break;
      case "calidad":
        document.getElementById("revision-de-calidad")?.scrollIntoView({ behavior: "smooth", block: "start" });
        break;
    }
  }

  function analizar() {
    iniciar.mutate(undefined, {
      onError: (error) => toast.error(getApiErrorMessage(error, "No pudimos iniciar el análisis. Inténtalo de nuevo.")),
    });
  }

  if (informe.isPending) {
    return (
      <div className="space-y-5 px-6 pt-2 pb-6 lg:px-8 lg:pb-8">
        <Skeleton className="h-6 w-64" />
        <Skeleton className="h-24 w-full rounded-xl" />
      </div>
    );
  }

  if (informe.isError || !informe.data) {
    return (
      <div className="space-y-5 px-6 pt-2 pb-6 lg:px-8 lg:pb-8">
        <EnlaceVolver to="/informes">Volver a informes</EnlaceVolver>
        <p className="text-danger-soft-foreground text-sm">No pudimos cargar el informe.</p>
      </div>
    );
  }

  const datos = informe.data;
  const volver = `/informes/${datos.id}`;

  if (!tieneTasacionEnCurso(datos.estado)) {
    return (
      <div className="space-y-5 px-6 pt-2 pb-6 lg:px-8 lg:pb-8">
        <EnlaceVolver to={volver}>Volver al expediente</EnlaceVolver>
        <p className="text-body text-ink-body max-w-prose text-pretty">
          La generación del informe se habilita cuando el cliente acepta la cotización: hasta entonces el informe es
          solo un presupuesto.
        </p>
      </div>
    );
  }

  const estadoFase1 = estadoDeLaFase1(corrida ?? null, dudas);
  const estadoFase2 = estadoDeLaFase2(valorizacion.data);
  const estadoFase3 = estadoDeLaFase3(redaccion.secciones.data);
  const estadoFase4 = estadoDeLaFase4(condiciones.data, emisiones.data);
  const fases: FaseDelCarril[] = [
    { numero: 1, nombre: "Análisis", tarea: "Responder las dudas", tono: estadoFase1.tono, estado: estadoFase1.texto },
    {
      numero: 2,
      nombre: "Valor",
      tarea: "Aceptar o corregir cada cifra",
      tono: estadoFase2.tono,
      estado: estadoFase2.texto,
    },
    {
      numero: 3,
      nombre: "Redacción",
      tarea: "Validar cada sección",
      tono: estadoFase3.tono,
      estado: estadoFase3.texto,
    },
    { numero: 4, nombre: "Documento", tarea: "Emitir", tono: estadoFase4.tono, estado: estadoFase4.texto },
  ];

  const pendientes = pendientesDeLaPantalla(dudas, valorizacion.data, redaccion.secciones.data);
  const destino = pendientes.destino;
  const analizando = corrida?.estado === "en_curso";
  const mostrarBanner = !bannerCerrado && !analizando && pendientes.hay && destino !== null;
  const bloquean = conteoDeDudas(dudas).bloquean;
  const primeraQueBloquea = filtrarDudas(dudas, "bloquean")[0];
  const encabezado = estadoDelEncabezado(corrida, condiciones.data, emisiones.data ?? []);

  function irAlaPrimeraQueBloquea() {
    setVista({ pestana: "dudas", filtro: "bloquean", dudaId: primeraQueBloquea?.id ?? null });
    setFase(1);
  }

  const subtitulo = [
    [datos.propiedad_nombre, datos.cliente_nombre].filter((x): x is string => Boolean(x)).join(" · "),
    datos.plantilla?.nombre ?? "",
  ].filter((x) => x !== "");

  return (
    <div className="space-y-5 px-6 pt-2 pb-6 lg:px-8 lg:pb-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1.5">
          <div className="flex flex-wrap items-center gap-[0.6875rem]">
            <span className="bg-divisor text-ink-muted rounded-md px-[0.5625rem] py-1 font-mono text-[0.6875rem] font-bold">
              {datos.numero_informe ?? `#${datos.id}`}
            </span>
            <span
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[0.6875rem] font-extrabold tracking-[0.04em]",
                CHIP_DEL_ENCABEZADO[encabezado.tono].chip,
              )}
            >
              <span aria-hidden className={cn("size-[0.4375rem] rounded-full", CHIP_DEL_ENCABEZADO[encabezado.tono].punto)} />
              {encabezado.texto}
            </span>
          </div>
          <p className="text-ink-medio flex flex-wrap items-center gap-x-2 text-xs font-semibold">
            {[...subtitulo, "Todo lo que valida se guarda al instante"].map((parte, i) => (
              <span key={parte} className="inline-flex items-center gap-2">
                {i > 0 ? (
                  <span aria-hidden className="text-ink-ghost">
                    ·
                  </span>
                ) : null}
                {parte}
              </span>
            ))}
          </p>
        </div>
        <Button asChild variant="secondary" size="sm">
          <Link to={volver}>
            <ChevronLeftIcon aria-hidden />
            Volver al expediente
          </Link>
        </Button>
      </div>

      {mostrarBanner && destino ? (
        <BannerDesdeSuUltimaSesion
          ultimaVisita={ultimaVisita}
          frases={pendientes.frases}
          textoDelBoton={destino.texto}
          onIr={() => {
            if (destino.fase === 1) setVista({ pestana: "dudas", filtro: destino.filtro, dudaId: destino.dudaId });
            setFase(destino.fase);
            setBannerCerrado(true);
          }}
          onCerrar={() => setBannerCerrado(true)}
        />
      ) : null}

      <CarrilDeFases fases={fases} activa={fase} onElegir={setFase} />

      {(fase === 2 || fase === 3) && bloquean > 0 ? (
        <AvisoDeDudasQueBloquean cantidad={bloquean} onIr={irAlaPrimeraQueBloquea} />
      ) : null}

      {fase === 1 ? (
        <AnalisisDelExpediente
          informeId={datos.id}
          corrida={analisis.isPending ? undefined : (corrida ?? null)}
          dudas={dudas}
          hechos={hechos}
          vista={vista}
          onVista={setVista}
          onAnalizar={analizar}
          onIrALaValorizacion={() => setFase(2)}
          lanzando={iniciar.isPending}
        />
      ) : fase === 2 ? (
        <ValorizacionDelInforme informeId={datos.id} />
      ) : fase === 3 ? (
        <RedaccionDelInforme
          informeId={datos.id}
          seccionInicialId={seccionInicial}
          onIrALasDudas={() => setFase(1)}
          onIrALaValorizacion={() => setFase(2)}
        />
      ) : (
        <DocumentoDelInforme
          informe={datos}
          condiciones={condiciones.data}
          cargandoCondiciones={condiciones.isPending}
          emisiones={emisiones.data ?? []}
          onIr={irADestino}
        />
      )}
    </div>
  );
}
